import { useEffect, useState } from "react";
import './coachQuestions.css';
import * as aserver from '../../APIClient';
import Button from '@material-ui/core/Button';

// The "get to know you" questionnaire shown to coaches once they've dealt with their
// policies, and always reachable afterwards from the User Profile page. This information
// helps us understand coaches' needs better. Columns live on CoachQuestionnaires (see
// models/coachQuestionnaire.js) - 'multiselect' answers are stored as comma-separated text.
export const questionDefinitions = [
    {
        field: 'yearsInField',
        type: 'select',
        label: 'How long have you worked in coaching or a related human development field?',
        options: ["I'm new to this field", 'Less than 2 years', '2–5 years', '6–10 years', '11–20 years', 'More than 20 years'],
    },
    {
        field: 'enneagramFamiliarity',
        type: 'select',
        label: 'How familiar are you with the Enneagram?',
        options: ['New to the Enneagram', 'Some personal exposure or self-study', 'Have completed Enneagram training other than Aephoria Level 1', 'I already use the Enneagram in my professional work', 'Teach, train or supervise others in the Enneagram'],
    },
    {
        field: 'developmentFrameworkFamiliarity',
        type: 'select',
        label: 'How familiar are you with vertical development or adult development stage theories?',
        options: ['New to these ideas', 'Some exposure or self-study', 'Have completed training in a vertical/adult development framework other than Aephoria Level 2', 'I already use these frameworks in my professional work', 'Teach, research or work extensively in this field'],
    },
    {
        field: 'systemLevels',
        type: 'multiselect',
        label: 'I work in the following levels of system',
        hint: 'Choose all that apply.',
        options: ['Individual', 'Teams', 'Pairs', 'Large scale systems change / Organisation level', 'Other (tell us more)'],
        otherOption: 'Other (tell us more)',
        otherField: 'systemLevelsOther',
    },
    {
        field: 'interestedTraining',
        type: 'multiselect',
        label: 'Are you interested in further Aephoria training?',
        hint: 'Pick as many as apply.',
        options: ['Maturity', 'Teams', 'Facilitation', 'Couples', 'Professional Pairs', 'Anything else'],
        otherOption: 'Anything else',
        otherField: 'interestedTrainingOther',
    },
    {
        field: 'mainWorkMode',
        type: 'select',
        label: 'How do you mainly work?',
        options: ['Independently / in my own practice', 'As part of a coaching or consulting collective', 'Within an organisation as an internal coach, consultant or people professional', 'None of these / not currently practising'],
    },
    {
        field: 'seniorLevelExperience',
        type: 'select',
        label: 'What is the most senior organisational level you have substantial experience working with?',
        options: ['Board', 'C-suite / Executive', 'Senior management', 'Middle management', 'Junior / first-line management', 'General employee level', 'I have little or no organisational/corporate experience'],
    },
    {
        field: 'corporateBackground',
        type: 'select',
        label: 'Before becoming a coach, have you worked in a corporate / organisational setting?',
        options: ['Yes', 'No'],
        followUp: {
            field: 'corporateBackgroundLevel',
            showWhen: 'Yes',
            label: 'If yes, specify the most senior organisational position you have held',
            options: ['Board', 'C-suite / Executive', 'Senior management', 'Middle management', 'Junior / first-line management', 'General employee level'],
        },
    },
    {
        field: 'otherAssessmentsUsed',
        type: 'shorttext',
        label: 'What other assessments do you frequently use in your work?',
    },
    {
        field: 'additionalInfo',
        type: 'longtext',
        label: 'Anything else you would like Aephoria to know about you so we can support you and your work best?',
    },
];

function emptyAnswers() {
    let answers = {};
    for (let q of questionDefinitions) {
        answers[q.field] = (q.type === 'multiselect') ? [] : '';
        if (q.otherField)
            answers[q.otherField] = '';
        if (q.followUp)
            answers[q.followUp.field] = '';
    }
    return (answers);
}

function answersToPayload(answers) {
    let payload = { ...answers };
    for (let q of questionDefinitions)
        if (q.type === 'multiselect')
            payload[q.field] = answers[q.field].join(',');
    return (payload);
}

function allSelectsAnswered(answers) {
    for (let q of questionDefinitions) {
        if (q.type !== 'select')
            continue;
        if (!answers[q.field])
            return false;
        if (q.followUp && (answers[q.field] === q.followUp.showWhen) && !answers[q.followUp.field])
            return false;
    }
    return true;
}

function payloadToAnswers(questionnaire) {
    let loaded = emptyAnswers();
    for (let field of Object.keys(loaded)) {
        if (!questionnaire[field])
            continue;
        let isMultiselect = questionDefinitions.some(q => (q.field === field) && (q.type === 'multiselect'));
        loaded[field] = isMultiselect ? questionnaire[field].split(',').filter(v => v) : questionnaire[field];
    }
    return (loaded);
}

const CoachQuestions = (props) => {
    const [answers, setAnswers] = useState(emptyAnswers());
    const [completedAt, setCompletedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                let response = await aserver.get(`api/v2/coaches/${props.coachUUID}/coachQuestionnaire`);
                if (!mounted)
                    return;
                let questionnaire = response.data.questionnaire;
                if (questionnaire) {
                    setAnswers(payloadToAnswers(questionnaire));
                    setCompletedAt(questionnaire.completedAt);
                }
            } catch (err) {
                console.log(err);
            }
            if (mounted)
                setLoading(false);
        };
        load();
        if (props.pingOnMount)
            aserver.post(`api/v2/coaches/${props.coachUUID}/coachQuestionnaire`, { complete: false });
        return () => { mounted = false };
        // eslint-disable-next-line
    }, [props.coachUUID]);

    function handleChange(field, value) {
        setAnswers(prev => ({ ...prev, [field]: value }));
    }

    function toggleMultiselect(q, option) {
        setAnswers(prev => {
            let selected = prev[q.field].includes(option) ? prev[q.field].filter(v => v !== option) : [...prev[q.field], option];
            let next = { ...prev, [q.field]: selected };
            if (q.otherField && (option === q.otherOption) && !selected.includes(q.otherOption))
                next[q.otherField] = '';
            return (next);
        });
    }

    async function submit(complete) {
        setSaving(true);
        try {
            await aserver.post(`api/v2/coaches/${props.coachUUID}/coachQuestionnaire`, { ...answersToPayload(answers), complete });
            if (complete)
                setCompletedAt(new Date().toString());
        } catch (err) {
            console.log(err);
        }
        setSaving(false);
        if (props.onDone)
            props.onDone(complete);
    }

    function renderQuestion(q) {
        if (q.type === 'select')
            return (<>
                <select className="coach-input" id={q.field} name={q.field} value={answers[q.field]} onChange={e => handleChange(q.field, e.target.value)}>
                    <option value="">Select an option...</option>
                    {q.options.map(option => <option key={option} value={option}>{option}</option>)}
                </select>
                {q.followUp && (answers[q.field] === q.followUp.showWhen) &&
                    <div className="coachQuestionFollowUp">
                        <label htmlFor={q.followUp.field}>{q.followUp.label}</label>
                        <select className="coach-input" id={q.followUp.field} name={q.followUp.field} value={answers[q.followUp.field]} onChange={e => handleChange(q.followUp.field, e.target.value)}>
                            <option value="">Select an option...</option>
                            {q.followUp.options.map(option => <option key={option} value={option}>{option}</option>)}
                        </select>
                    </div>
                }
            </>);
        if (q.type === 'multiselect')
            return (<>
                {q.options.map(option =>
                    <div className="coachQuestionCheckbox" key={option}>
                        <input type="checkbox" id={`${q.field}_${option}`} checked={answers[q.field].includes(option)} onChange={() => toggleMultiselect(q, option)} />
                        <label htmlFor={`${q.field}_${option}`}>{option}</label>
                    </div>
                )}
                {q.otherField && answers[q.field].includes(q.otherOption) &&
                    <input className="coach-input" type="text" maxLength={500} placeholder="tell us more" value={answers[q.otherField]} onChange={e => handleChange(q.otherField, e.target.value)} />
                }
            </>);
        if (q.type === 'shorttext')
            return (<input className="coach-input" type="text" id={q.field} name={q.field} maxLength={500} value={answers[q.field]} onChange={e => handleChange(q.field, e.target.value)} placeholder="enter here" />);
        // longtext
        return (<textarea className="coach-input" id={q.field} name={q.field} maxLength={2000} value={answers[q.field]} onChange={e => handleChange(q.field, e.target.value)} placeholder="enter here" />);
    }

    return (
        <div className="coachQuestionsContainer">
            {loading && <p>Loading your previous answers, if any...</p>}
            {completedAt && <p className="coachQuestionsCompletedNote">Thanks - you've already answered these. Feel free to update your answers below at any time.</p>}
            <p>This information helps us understand your needs better.</p>
            {questionDefinitions.map(q =>
                <div className="coachQuestion" key={q.field}>
                    <label htmlFor={q.field}>{q.label}</label>
                    {q.hint && <div className="coachQuestionHint">{q.hint}</div>}
                    {renderQuestion(q)}
                </div>
            )}
            <div className="coachQuestionsButtons">
                <Button variant="contained" color="primary" disabled={saving || !allSelectsAnswered(answers)} onClick={() => submit(true)}>{saving ? 'Please wait...' : 'Submit'}</Button>
                {props.allowSkip && <Button variant="contained" color="primary" onClick={() => submit(false)}>Answer later</Button>}
            </div>
        </div>
    )
};

export default CoachQuestions;
